import React, {useEffect} from 'react';
import Loader from './loader';
import HoverImageTile from './hoverimagetile';
import {parseDate, dateMonthShort, dateDayZeroPadded} from './util';
import HighlightedText from './highlightedtext';
import TermCloud from './termcloud';
import ArrowButton from './arrow_button';

/*
    SICArchive

    Renders an archive of posts
    Used for all post types
*/

const {
    loadMoreContent,
    util: {executeAfter},
    hooks: {useSelector, useBottom},
    css: {prefixCSS}
} = polyphony;

const styles = {
    outerContainer: prefixCSS({
        display: 'flex',
        flexFlow: 'row wrap'
    }),
    innerContainer: prefixCSS({
        display: 'flex',
        flexFlow: 'column nowrap',
        maxWidth: '100%',
        boxSizing: 'border-box'
    }),
    loadingContainer: prefixCSS({
        width: '100%',
        display: 'flex',
        justifyContent: 'center'
    })
};

const SICArchive = ({postType, taxonomies}) => { // eslint-disable-line no-unused-vars
    const totalCount = useSelector(s => s.total_count);
    const posts = useSelector(s => s.posts);
    const isLoading = useSelector(s => s.loadingMore || s.loadingReplacement);

    const isBottom = useBottom(() => {
        let offset = 100;
        let footer = document.querySelector('.footer');
        if (footer) {
            offset += footer.getBoundingClientRect().height;
        }
        return offset;
    });

    useEffect(() => {
        if (isBottom && posts.length < totalCount) {
            const load = () => {
                loadMoreContent();
                return () => {};
            };
            const loadMore = () => isLoading ? executeAfter(loadMore, 100) : load();
            return loadMore();
        }
        return () => {};
    }, [isBottom, totalCount, posts.length]);

    return (
        <div role='feed'>
            <TermCloud />
            {posts.map((post, idx) => <ArchivePost isLast={idx === posts.length - 1} post={post} key={idx} />)}
            {isLoading &&
            <div key="loader" role="none" style={styles.loaderContainer}>
                <Loader />
            </div>
            }
        </div>
    );
};

SICArchive.displayName = 'SICArchive';

const pstyles = {
    outerContainer: {
        position: 'relative',
        height: 400,
        overflow: 'hidden'
    },
    innerContainer: prefixCSS({
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        boxSizing: 'border-box',
        position: 'absolute',
        display: 'flex',
        flexFlow: 'column nowrap',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        padding: 40
    })
};

const ArchivePost = ({isLast, post: {title, post_type, post_date_iso, permalink, featured_media, taxonomies, meta:{event_start_timestamp}}}) => {
    const dateFontSizeDay = useSelector(({measurements: m}) => m.post_archive_font_size_date_day || 35);
    const dateFontSizeMonth = useSelector(({measurements: m}) => m.post_archive_font_size_date_month || 21);
    const fontSize = useSelector(({measurements: m}) => m.post_archive_font_size);

    const isEvent = post_type.name === 'event';
    const date = parseDate(isEvent ? event_start_timestamp : post_date_iso);
    let categories = taxonomies[isEvent ? 'event_category' : 'category'].filter(c => c.slug !== 'uncategorized').map(c => c.name);

    return <HoverImageTile role='article' media={featured_media.default} href={permalink} style={{...pstyles.outerContainer, marginBottom: !isLast ? 50 : undefined}}>
        <div style={pstyles.innerContainer} role="none">
            <div role="none" style={{margin: 5, display: 'flex', flexFlow: 'column nowrap', alignItems: 'center'}}>
                <span style={{textShadow: 'black 1px 0px 5px', fontSize: dateFontSizeDay, textAlign: 'right'}}>{dateDayZeroPadded(date)}</span><span style={{textShadow: 'black 1px 0px 5px', fontSize: dateFontSizeMonth, textAlign: 'right', textTransform: 'uppercase'}}>{dateMonthShort(date)}</span>
            </div>
            <div style={{marginLeft: 10}} role="none">
                <HighlightedText clamp={false} style={{width: '100%', fontSize: `calc(60% * ${fontSize})`, top: 3}}>{categories.join(', ')}</HighlightedText>
                <HighlightedText clamp={false} style={{fontSize}}>{title}</HighlightedText>
            </div>
            <ArrowButton tag='div' style={{marginTop: 10}}>Read more</ArrowButton>
        </div>
    </HoverImageTile>;
};

ArchivePost.displayName = 'ArchivePost';

export default SICArchive;

